import React, { PureComponent } from 'react';
import { Link } from 'react-router';
import styled, { keyframes, css } from 'styled-components';

import Auth from '../../../routes/Auth';

import WhatToExpectStep2 from './what-to-expect/step2';
import WhatToExpectStep3 from './what-to-expect/step3';
import WhatToExpectStep4 from './what-to-expect/step4';

// Consolidated instructor welcome checklist.
//
// Mounts at /instructor/welcome and replaces the prior
// multi-page wizard (Welcome > Game Overview > Setting Up Gameplay
// > How Grading Works > What To Expect > How We Support You >
// Next Steps). One page, three top-level steps, substeps inline:
//
//   Step 1, Required Setup
//     - Set the round deadlines               (link → /instructor/rounds)
//     - Set the teams                         (link → /instructor/teams-roles)
//
//   Step 2, Review Default Settings (optional modifications)
//     - Review default round metrics & targets
//                                             (link → /instructor/grading/team-score)
//     - Review default grading weights        (link → /instructor/grading/overall-score)
//     - Review default team evaluation        (link → /instructor/peer-review)
//
//   Step 3, What to Expect (per-round popups)
//     - Round 1, Product & Marketing
//     - Round 2, Operations
//     - Round 3, Accounting & Finance
//
// This page is the same for all rounds; completion is persisted to
// localStorage keyed by user id only (no per-round key) since the
// instructor's setup decisions span the whole course.

// ---------- copy / data --------------------------------------------

const COMPLETION_COPY = {
  icon: '✓',
  title: 'Setup complete!',
  description:
    'Great work. You can revisit any step at any time from this checklist.',
};

const STEP1_SUBSTEPS = [
  {
    id: 'set-round-deadlines',
    title: 'Set the round deadlines',
    description:
      'Open the rounds page to set start and end dates for each round in this challenge.',
    linkText: 'Set round deadlines',
    href: '/instructor/rounds',
  },
  {
    id: 'set-teams-roles',
    title: 'Set the teams',
    description:
      'Assign students to teams and roles before the first round begins.',
    linkText: 'Set teams & roles',
    href: '/instructor/teams-roles',
  },
];

const STEP2_SUBSTEPS = [
  {
    id: 'review-metrics-targets',
    title: 'Review default round metrics & targets',
    description:
      'Each round comes with default financial metrics and targets. Review them and modify if you want a different challenge profile.',
    linkText: 'Review metrics & targets',
    href: '/instructor/grading/team-score',
  },
  {
    id: 'review-grading-weights',
    title: 'Review default grading weights',
    description:
      'Adjust the weight of each round (round weight) and the weight of each activity score (score weight) if the defaults don\'t fit your course.',
    linkText: 'Review grading weights',
    href: '/instructor/grading/overall-score',
  },
  {
    id: 'review-team-evaluation',
    title: 'Review default team evaluation',
    description:
      'Review the questions and timing, and modify if you want a different structure.',
    linkText: 'Review team evaluation',
    href: '/instructor/peer-review',
  },
];

const STEP3_SUBSTEPS = [
  {
    id: 'whattoexpect-round1',
    title: 'Round 1, Product & Marketing',
    description:
      'Product and marketing decisions are emphasized in the first round.',
    linkText: "See what's in Round 1",
    popupId: 'round1',
  },
  {
    id: 'whattoexpect-round2',
    title: 'Round 2, Operations',
    description:
      'Operations decisions take center stage in the second round.',
    linkText: "See what's in Round 2",
    popupId: 'round2',
  },
  {
    id: 'whattoexpect-round3',
    title: 'Round 3, Accounting & Finance',
    description:
      'Accounting and finance decisions come into focus in the final round.',
    linkText: "See what's in Round 3",
    popupId: 'round3',
  },
];

const POPUP_COMPONENTS = {
  round1: WhatToExpectStep2,
  round2: WhatToExpectStep3,
  round3: WhatToExpectStep4,
};

const POPUP_TITLES = {
  round1: 'Round 1, Product & Marketing',
  round2: 'Round 2, Operations',
  round3: 'Round 3, Accounting & Finance',
};

// Shared (not per-round). The instructor's setup decisions span
// the whole course.
const storageKey = () => {
  const uid = Auth.getUserId();
  return `instructor-onboarding:checklist:${uid || 'anon'}`;
};

// ---------- styles -------------------------------------------------

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Styled = {};

Styled.Root = styled.section`
  background: #FAFBFD;
  min-height: 100%;
  margin: 0;
  padding: 64px 32px 24px;
  font-family: 'Inter', 'Mukta Vaani', sans-serif;
  color: #0F172A;
`;

Styled.Header = styled.div`
  max-width: 760px;
  margin: 0 auto 16px;
  text-align: center;
`;

Styled.Title = styled.h1`
  margin: 0 0 14px;
  font-family: 'Bricolage Grotesque', 'Mukta Vaani', sans-serif;
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: #0F172A;
  text-align: center;
`;

Styled.Subhead = styled.p`
  margin: 0 0 14px;
  font-size: 15px;
  color: #475569;
  line-height: 1.4;
  text-align: center;
`;

Styled.Timeline = styled.ol`
  position: relative;
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 944px;
`;

Styled.Step = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 18px;
  padding: 10px 14px 10px 6px;
  margin-bottom: 12px;
  border-radius: 14px;
  background: transparent;
  transition: background 200ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 25px;
    width: 2px;
    top: 0;
    bottom: -12px;
    background: #E2E8F0;
    z-index: 0;
  }

  &:first-child::before {
    top: 30px;
  }

  &:last-child::before {
    bottom: auto;
    height: 30px;
  }

  ${p => p.active && css`
    background: #DBEAFE;
  `}

  &:last-child {
    margin-bottom: 0;
  }

  /* Substeps stay collapsed until the instructor hovers anywhere
     within the Step's bounds. Animated via max-height + opacity so
     the rest of the page doesn't jolt when they appear/disappear.
     Display:none on focus-within would break keyboard access, so we
     also expand on focus-within (Tab key reveals the substeps for
     keyboard users). */
  .checklist-substep-group {
    max-height: 0;
    opacity: 0;
    margin-top: 0;
    overflow: hidden;
    transition: max-height 250ms ease, opacity 200ms ease, margin-top 250ms ease;
  }

  &:hover .checklist-substep-group,
  &:focus-within .checklist-substep-group {
    max-height: 800px;
    opacity: 1;
    margin-top: 8px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    .checklist-substep-group {
      transition: none;
    }
  }
`;

const stepCircleBase = `
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', 'Mukta Vaani', sans-serif;
  font-size: 18px;
  font-weight: 700;
  background: #FFFFFF;
  border: 2px solid #E2E8F0;
  color: #64748B;
  position: relative;
  z-index: 1;
  padding: 0;
  transition: background 200ms ease, border-color 200ms ease, color 200ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.StepCircleStatic = styled.div`
  ${stepCircleBase}

  ${p => p.completed && css`
    background: #10B981;
    border-color: #10B981;
    color: #FFFFFF;
  `}

  ${p => (!p.completed && p.active) && css`
    background: #EFF6FF;
    border-color: #2563EB;
    color: #2563EB;
  `}
`;

Styled.StepBody = styled.div`
  padding-top: 2px;
`;

Styled.StepTitle = styled.h2`
  margin: 0 0 4px;
  font-family: 'Bricolage Grotesque', 'Mukta Vaani', sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #0F172A;
  transition: color 300ms ease, text-decoration-color 300ms ease;

  ${p => p.completed && css`
    color: #94A3B8;
    text-decoration: line-through;
    text-decoration-color: #CBD5E1;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.StepDescription = styled.p`
  margin: 0 0 6px;
  font-size: 15px;
  line-height: 1.4;
  color: #475569;
  max-width: 900px;
`;

// Render sites pass className="checklist-substep-group" so the
// parent Styled.Step's :hover reveal rule can target this group
// by class. Styled.Step is defined before this component in the
// file, so we can't use styled-components' reference-by-component
// trick to do the targeting purely via interpolation.
Styled.SubstepGroup = styled.div`
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 4px;
  /* margin-top moves to the hover rule on Step so the spacing
     animates in alongside the reveal. */
`;

Styled.SubstepRow = styled.button`
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  transition: background 150ms ease;

  & + & {
    border-top: 1px solid #E2E8F0;
  }

  &:hover {
    background: #FFFFFF;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: -2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.Checkbox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border: 2px solid #CBD5E1;
  border-radius: 5px;
  background: #FFFFFF;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  transition: background 200ms ease, border-color 200ms ease;

  ${p => p.checked && css`
    background: #10B981;
    border-color: #10B981;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.SubstepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

Styled.SubstepTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #0F172A;
  transition: color 300ms ease, text-decoration-color 300ms ease;

  ${p => p.completed && css`
    color: #94A3B8;
    text-decoration: line-through;
    text-decoration-color: #CBD5E1;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.SubstepDescription = styled.span`
  font-size: 14px;
  line-height: 1.4;
  color: #475569;
`;

Styled.SubstepLink = styled.a`
  font-size: 14px;
  font-weight: 700;
  color: #2563EB;
  text-decoration: none;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

Styled.CompletionBanner = styled.div`
  max-width: 760px;
  margin: 32px auto 0;
  padding: 28px 32px;
  border-radius: 20px;
  background: linear-gradient(135deg, #ECFDF5, #D1FAE5);
  border: 1px solid #A7F3D0;
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 20px;
  align-items: center;
  animation: ${fadeUp} 500ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

Styled.CompletionIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #10B981;
  color: #FFFFFF;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
`;

Styled.CompletionTitle = styled.h3`
  margin: 0 0 4px;
  font-family: 'Bricolage Grotesque', 'Mukta Vaani', sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #064E3B;
`;

Styled.CompletionDescription = styled.p`
  margin: 0;
  font-size: 15px;
  color: #047857;
  line-height: 1.5;
`;

Styled.SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// ---------- modal shell -------------------------------------------

Styled.Modal = {};

Styled.Modal.Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  z-index: 1000;
  animation: ${fadeIn} 200ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

Styled.Modal.Card = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  font-family: 'Inter', 'Mukta Vaani', sans-serif;
  overflow: hidden;
`;

Styled.Modal.HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 1px solid #E2E8F0;
`;

Styled.Modal.Title = styled.h2`
  margin: 0;
  font-family: 'Bricolage Grotesque', 'Mukta Vaani', sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #0F172A;
`;

Styled.Modal.CloseButton = styled.button`
  background: transparent;
  border: 0;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748B;
  font-size: 22px;
  line-height: 1;

  &:hover {
    background: #F1F5F9;
    color: #0F172A;
  }
`;

Styled.Modal.Body = styled.div`
  overflow-y: auto;
  padding: 0;
  font-size: 16px;
  line-height: 1.55;
  color: #1F2A44;
`;

// ---------- component ---------------------------------------------

class InstructorWelcomeChecklist extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      completed: this.readFromStorage(),
      activePopupId: null,
    };
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.handleKeydown);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleKeydown);
    }
  }

  handleKeydown = (e) => {
    if (e.key === 'Escape' && this.state.activePopupId) {
      this.closePopup();
    }
  };

  // ---- persistence -----------------------------------------------

  readFromStorage = () => {
    try {
      const raw = window.localStorage.getItem(storageKey());
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return {};
      const next = {};
      parsed.forEach((id) => {
        if (typeof id === 'string') next[id] = true;
      });
      return next;
    } catch (e) {
      return {};
    }
  };

  writeToStorage = (completed) => {
    try {
      const ids = Object.keys(completed).filter(k => completed[k] === true);
      window.localStorage.setItem(storageKey(), JSON.stringify(ids));
    } catch (e) {
      // localStorage unavailable. Silent fail. In-memory state still drives this
      // render, we just lose persistence across reloads.
    }
  };

  toggle = (id) => {
    this.setState((prev) => {
      const next = { ...prev.completed };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.writeToStorage(next);
      return { completed: next };
    });
  };

  // ---- helpers ---------------------------------------------------

  get activeRound() {
    const { roundInUrl, currentRound } = this.props;
    return roundInUrl || currentRound || 1;
  }

  withRound = (path) => {
    return `${path}${path.indexOf('?') >= 0 ? '&' : '?'}round=${this.activeRound}`;
  };

  isLeafComplete = (id) => !!this.state.completed[id];

  isStepComplete = (substeps) => {
    return substeps.every(s => this.isLeafComplete(s.id));
  };

  firstIncompleteStepId = () => {
    if (!this.isStepComplete(STEP1_SUBSTEPS)) return 'required-setup';
    if (!this.isStepComplete(STEP2_SUBSTEPS)) return 'review-defaults';
    if (!this.isStepComplete(STEP3_SUBSTEPS)) return 'what-to-expect';
    return null;
  };

  allCompleteIds = () => {
    return [...STEP1_SUBSTEPS, ...STEP2_SUBSTEPS, ...STEP3_SUBSTEPS].map(s => s.id);
  };

  allComplete = () => {
    return this.allCompleteIds().every(id => this.isLeafComplete(id));
  };

  // ---- popup actions ---------------------------------------------

  openPopup = (id) => {
    this.setState({ activePopupId: id });
  };

  closePopup = () => {
    this.setState({ activePopupId: null });
  };

  stopRowClickPropagation = (e) => {
    if (e) e.stopPropagation();
  };

  // ---- render helpers --------------------------------------------

  renderStepCircle = (number, completed, active) => {
    const content = completed ? '✓' : number;
    return (
      <Styled.StepCircleStatic completed={completed} active={active} aria-hidden="true">
        {content}
      </Styled.StepCircleStatic>
    );
  };

  // Substeps in Step 1 + Step 2 navigate to existing instructor
  // routes. The link includes the current round when the route is
  // round-aware.
  renderInternalSubstepLink = (text, href, withRound) => {
    const finalHref = withRound ? this.withRound(href) : href;
    return (
      <Link to={finalHref} onClick={this.stopRowClickPropagation} style={{ textDecoration: 'none' }}>
        <Styled.SubstepLink as="span">
          {text} →
        </Styled.SubstepLink>
      </Link>
    );
  };

  renderPopupSubstepLink = (text, popupId) => (
    <Styled.SubstepLink
      as="button"
      type="button"
      onClick={(e) => {
        this.stopRowClickPropagation(e);
        this.openPopup(popupId);
      }}
    >
      {text} →
    </Styled.SubstepLink>
  );

  renderSubstepRow = (sub, linkNode) => {
    const isDone = this.isLeafComplete(sub.id);
    const label = isDone
      ? `Mark ${sub.title} as incomplete`
      : `Mark ${sub.title} as complete`;
    return (
      <Styled.SubstepRow
        key={sub.id}
        type="button"
        onClick={() => this.toggle(sub.id)}
        aria-pressed={isDone}
        aria-label={label}
      >
        <Styled.Checkbox checked={isDone} aria-hidden="true">
          {isDone ? '✓' : ''}
        </Styled.Checkbox>
        <Styled.SubstepBody>
          <Styled.SubstepTitle completed={isDone}>{sub.title}</Styled.SubstepTitle>
          {/* Link sits inline with description (single line where
              possible) so the full checklist fits one viewport. */}
          <Styled.SubstepDescription>
            {sub.description}
            {linkNode && <span>{' '}{linkNode}</span>}
          </Styled.SubstepDescription>
        </Styled.SubstepBody>
      </Styled.SubstepRow>
    );
  };

  // /instructor/rounds (and /grading/* + /peer-review) use ?round=N
  // for round-aware routing. /instructor/teams-roles does not.
  isRoundAwareRoute = (href) => {
    if (!href) return false;
    if (href.indexOf('/instructor/teams-roles') === 0) return false;
    if (href.indexOf('/instructor/peer-review') === 0) return false;
    return true;
  };

  renderStep1Substeps = () => (
    <Styled.SubstepGroup className="checklist-substep-group">
      {STEP1_SUBSTEPS.map(sub => this.renderSubstepRow(
        sub,
        this.renderInternalSubstepLink(
          sub.linkText,
          sub.href,
          this.isRoundAwareRoute(sub.href),
        ),
      ))}
    </Styled.SubstepGroup>
  );

  renderStep2Substeps = () => (
    <Styled.SubstepGroup className="checklist-substep-group">
      {STEP2_SUBSTEPS.map(sub => this.renderSubstepRow(
        sub,
        this.renderInternalSubstepLink(
          sub.linkText,
          sub.href,
          this.isRoundAwareRoute(sub.href),
        ),
      ))}
    </Styled.SubstepGroup>
  );

  renderStep3Substeps = () => (
    <Styled.SubstepGroup className="checklist-substep-group">
      {STEP3_SUBSTEPS.map(sub => this.renderSubstepRow(
        sub,
        this.renderPopupSubstepLink(sub.linkText, sub.popupId),
      ))}
    </Styled.SubstepGroup>
  );

  renderPopup = () => {
    const { activePopupId } = this.state;
    if (!activePopupId) return null;
    const Component = POPUP_COMPONENTS[activePopupId];
    if (!Component) return null;
    return (
      <Styled.Modal.Backdrop onClick={this.closePopup} role="dialog" aria-modal="true">
        <Styled.Modal.Card onClick={e => e.stopPropagation()}>
          <Styled.Modal.HeaderBar>
            <Styled.Modal.Title>{POPUP_TITLES[activePopupId]}</Styled.Modal.Title>
            <Styled.Modal.CloseButton
              type="button"
              onClick={this.closePopup}
              aria-label="Close"
            >
              ×
            </Styled.Modal.CloseButton>
          </Styled.Modal.HeaderBar>
          <Styled.Modal.Body>
            <Component />
          </Styled.Modal.Body>
        </Styled.Modal.Card>
      </Styled.Modal.Backdrop>
    );
  };

  // ---- render ----------------------------------------------------

  render() {
    const step1Complete = this.isStepComplete(STEP1_SUBSTEPS);
    const step2Complete = this.isStepComplete(STEP2_SUBSTEPS);
    const step3Complete = this.isStepComplete(STEP3_SUBSTEPS);
    const activeId = this.firstIncompleteStepId();
    const allDone = this.allComplete();

    const completedCount = this.allCompleteIds()
      .filter(id => this.isLeafComplete(id)).length;
    const totalCount = this.allCompleteIds().length;

    return (
      <Styled.Root>
        <Styled.Header>
          <Styled.Title>Welcome to the Intro to Business Challenge!</Styled.Title>
          <Styled.Subhead>
            To get the course set up and ready, follow the checklist below.
          </Styled.Subhead>
          <Styled.SrOnly aria-live="polite">
            {completedCount} of {totalCount} steps complete
          </Styled.SrOnly>
        </Styled.Header>

        <Styled.Timeline>
          {/* Step 1, Required Setup */}
          <Styled.Step active={activeId === 'required-setup'}>
            {this.renderStepCircle(1, step1Complete, activeId === 'required-setup')}
            <Styled.StepBody>
              <Styled.StepTitle completed={step1Complete}>
                Required Setup
              </Styled.StepTitle>
              <Styled.StepDescription>
                Take care of these before students join, they unlock the round and team structure.
              </Styled.StepDescription>
              {this.renderStep1Substeps()}
            </Styled.StepBody>
          </Styled.Step>

          {/* Step 2, Review Defaults */}
          <Styled.Step active={activeId === 'review-defaults'}>
            {this.renderStepCircle(2, step2Complete, activeId === 'review-defaults')}
            <Styled.StepBody>
              <Styled.StepTitle completed={step2Complete}>
                Review Default Settings
              </Styled.StepTitle>
              <Styled.StepDescription>
                The defaults are sensible. Skim each item and only modify if you want a different challenge profile.
              </Styled.StepDescription>
              {this.renderStep2Substeps()}
            </Styled.StepBody>
          </Styled.Step>

          {/* Step 3, What to Expect */}
          <Styled.Step active={activeId === 'what-to-expect'}>
            {this.renderStepCircle(3, step3Complete, activeId === 'what-to-expect')}
            <Styled.StepBody>
              <Styled.StepTitle completed={step3Complete}>
                What to Expect
              </Styled.StepTitle>
              <Styled.StepDescription>
                Each round emphasizes a different business discipline. Click into each round to preview what students will see.
              </Styled.StepDescription>
              {this.renderStep3Substeps()}
            </Styled.StepBody>
          </Styled.Step>
        </Styled.Timeline>

        {allDone && (
          <Styled.CompletionBanner>
            <Styled.CompletionIcon aria-hidden="true">
              {COMPLETION_COPY.icon}
            </Styled.CompletionIcon>
            <div>
              <Styled.CompletionTitle>{COMPLETION_COPY.title}</Styled.CompletionTitle>
              <Styled.CompletionDescription>
                {COMPLETION_COPY.description}
              </Styled.CompletionDescription>
            </div>
          </Styled.CompletionBanner>
        )}

        {this.renderPopup()}
      </Styled.Root>
    );
  }
}

export default InstructorWelcomeChecklist;



// WEBPACK FOOTER //
// ./src/instructor/components/welcome/WelcomeChecklist.js